import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, labelStyle, outlineButton, ghostButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const STORAGE_BASE = 'https://jfartqkalyqjwyueqfet.supabase.co/storage/v1/object/public/photos';

const T = {
    fr: {
        albums: 'Albums',
        loading: 'Chargement...',
        empty: 'Aucun album.',
        enabled: 'Cartes postales activées',
        disabled: 'Désactivé',
        deactivate: 'Désactiver',
        activate: 'Activer',
        selectAlbum: 'Sélectionne un album pour choisir ses photos.',
        close: 'Fermer',
        enableFirst: 'Active cet album d\'abord (bouton "Activer" à gauche) pour pouvoir choisir des photos.',
        loadingPhotos: 'Chargement des photos...',
        noPhotos: 'Aucune photo dans cet album.',
        selected: (n) => `${n} photo${n !== 1 ? 's' : ''} sélectionnée${n !== 1 ? 's' : ''} — clique pour activer/désactiver`,
        enabledFor: (title) => `${title} activé pour les cartes postales`,
        disabledFor: (title) => `${title} désactivé`,
    },
    en: {
        albums: 'Albums',
        loading: 'Loading...',
        empty: 'No albums.',
        enabled: 'Postcards enabled',
        disabled: 'Disabled',
        deactivate: 'Deactivate',
        activate: 'Activate',
        selectAlbum: 'Select an album to choose its photos.',
        close: 'Close',
        enableFirst: 'Enable this album first (the "Activate" button on the left) to choose photos.',
        loadingPhotos: 'Loading photos...',
        noPhotos: 'No photos in this album.',
        selected: (n) => `${n} photo${n !== 1 ? 's' : ''} selected — click to toggle`,
        enabledFor: (title) => `${title} enabled for postcards`,
        disabledFor: (title) => `${title} disabled`,
    },
};

function AdminPostcards() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [albums, setAlbums] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAlbum, setSelectedAlbum] = useState(null);
    const [albumPhotos, setAlbumPhotos] = useState([]);
    const [loadingPhotos, setLoadingPhotos] = useState(false);
    const [eligiblePhotoUrls, setEligiblePhotoUrls] = useState(new Set());
    const [statusMsg, setStatusMsg] = useState('');

    const fetchAlbums = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('albums')
            .select('folder, region, title, cover_url, postcard_eligible')
            .eq('archived', false)
            .order('date', { ascending: false });
        if (!error && data) setAlbums(data);
        setLoading(false);
    };

    useEffect(() => { fetchAlbums(); }, []);

    const openAlbum = async (album) => {
        setSelectedAlbum(album);
        setLoadingPhotos(true);

        const { data: files } = await supabase.storage.from('photos').list(`${album.region}/${album.folder}`);
        const urls = (files || [])
            .filter(f => f.name !== '.emptyFolderPlaceholder')
            .map(f => ({ name: f.name, url: `${STORAGE_BASE}/${album.region}/${album.folder}/${f.name}` }));
        setAlbumPhotos(urls);

        const { data: eligibleRows } = await supabase
            .from('postcard_photos')
            .select('photo_url')
            .eq('album_folder', album.folder);
        setEligiblePhotoUrls(new Set((eligibleRows || []).map(r => r.photo_url)));

        setLoadingPhotos(false);
    };

    const handleToggleAlbumEligible = async (album) => {
        const newValue = !album.postcard_eligible;
        await supabase.from('albums').update({ postcard_eligible: newValue }).eq('folder', album.folder);
        setAlbums(prev => prev.map(a => a.folder === album.folder ? { ...a, postcard_eligible: newValue } : a));
        if (selectedAlbum?.folder === album.folder) {
            setSelectedAlbum({ ...selectedAlbum, postcard_eligible: newValue });
        }
        setStatusMsg(newValue ? t.enabledFor(album.title) : t.disabledFor(album.title));
    };

    const handleTogglePhoto = async (photo) => {
        const isEligible = eligiblePhotoUrls.has(photo.url);
        if (isEligible) {
            await supabase
                .from('postcard_photos')
                .delete()
                .eq('album_folder', selectedAlbum.folder)
                .eq('photo_url', photo.url);
            setEligiblePhotoUrls(prev => {
                const next = new Set(prev);
                next.delete(photo.url);
                return next;
            });
        } else {
            await supabase
                .from('postcard_photos')
                .insert({ album_folder: selectedAlbum.folder, photo_url: photo.url });
            setEligiblePhotoUrls(prev => new Set(prev).add(photo.url));
        }
    };

    return (
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

            <div style={{ flex: '1 1 320px', minWidth: '280px' }}>
                <p style={{ ...labelStyle, marginBottom: '14px' }}>{t.albums}</p>

                {loading ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
                ) : albums.length === 0 ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.empty}</p>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {albums.map(a => (
                            <div key={a.folder} style={{
                                display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                                background: theme.card,
                                border: selectedAlbum?.folder === a.folder ? `1px solid ${theme.text}` : `1px solid ${theme.border}`,
                                borderRadius: 0,
                                cursor: 'pointer',
                            }} onClick={() => openAlbum(a)}>
                                <div style={{ width: '44px', height: '44px', borderRadius: 0, overflow: 'hidden', flexShrink: 0, background: theme.bg }}>
                                    {a.cover_url && <img src={a.cover_url} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '13px', color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {a.title}
                                    </p>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '10.5px', color: a.postcard_eligible ? '#0e1013' : theme.textMuted }}>
                                        {a.postcard_eligible ? t.enabled : t.disabled}
                                    </p>
                                </div>
                                <button
                                    className="wlink"
                                    onClick={(e) => { e.stopPropagation(); handleToggleAlbumEligible(a); }}
                                    style={{ ...outlineButton, padding: '5px 10px', fontSize: '10.5px', flexShrink: 0 }}
                                >
                                    {a.postcard_eligible ? t.deactivate : t.activate}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, marginTop: '12px' }}>{statusMsg}</p>}
            </div>

            <div style={{ flex: '1 1 400px', minWidth: '320px' }}>
                {!selectedAlbum ? (
                    <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>
                        {t.selectAlbum}
                    </p>
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                            <p style={{ ...labelStyle, marginBottom: 0 }}>{selectedAlbum.title}</p>
                            <button className="wlink" onClick={() => setSelectedAlbum(null)} style={ghostButton}>{t.close}</button>
                        </div>

                        {!selectedAlbum.postcard_eligible ? (
                            <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>
                                {t.enableFirst}
                            </p>
                        ) : loadingPhotos ? (
                            <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loadingPhotos}</p>
                        ) : albumPhotos.length === 0 ? (
                            <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.noPhotos}</p>
                        ) : (
                            <>
                                <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, marginBottom: '10px' }}>
                                    {t.selected(eligiblePhotoUrls.size)}
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
                                    {albumPhotos.map(photo => {
                                        const isEligible = eligiblePhotoUrls.has(photo.url);
                                        return (
                                            <div
                                                key={photo.name}
                                                onClick={() => handleTogglePhoto(photo)}
                                                style={{
                                                    position: 'relative',
                                                    aspectRatio: '1',
                                                    borderRadius: 0,
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    border: isEligible ? `2px solid #0e1013` : `1px solid ${theme.border}`,
                                                }}
                                            >
                                                <img src={photo.url} alt={photo.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                {isEligible && (
                                                    <div style={{
                                                        position: 'absolute', top: '4px', right: '4px',
                                                        background: '#0e1013', color: 'white', borderRadius: 0,
                                                        width: '18px', height: '18px', display: 'flex',
                                                        alignItems: 'center', justifyContent: 'center', fontSize: '10px',
                                                    }}>✓</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default AdminPostcards;